import { useEffect, useRef, useState, useCallback } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';
import { bulkImportProgress } from './bulkImportProgress';

const BULK_IMPORT_UPDATED_EVENT = 'cartlens:bulk-import-updated';
export const notifyBulkImportUpdated = () => window.dispatchEvent(new Event(BULK_IMPORT_UPDATED_EVENT));

// Pro/Ultra only (2026-07-21 policy change) — Free tier no longer has access
// to Bulk Import at all; gated at the entry point (App.tsx/AppHeader), not here.
export const BULK_IMPORT_CAP = { pro: 20, ultra: 20 } as const;

export const getBulkImportCap = (isProUser: boolean, isUltraUser: boolean): number => {
  if (isUltraUser) return BULK_IMPORT_CAP.ultra;
  return BULK_IMPORT_CAP.pro;
};

/**
 * Runs `worker` over `items` with at most `limit` in flight at once.
 * A single failure does not stop the rest of the batch — the caller's worker
 * is expected to catch and record its own per-item failure state.
 * Only used for bounding parallel Storage uploads now — extraction/archive
 * moved server-side, so this no longer runs the actual processing pipeline.
 */
export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>
): Promise<void> {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      await worker(items[index], index);
    }
  });
  await Promise.all(runners);
}

export type BulkReceiptStatus =
  | 'queued' | 'processing' | 'extracted' | 'failed' | 'blocked'
  | 'confirmed' | 'archiving' | 'archived';

export interface BulkImportReceiptRow {
  id: string;
  batchId: string;
  imagePath: string;
  status: BulkReceiptStatus;
  storeName: string | null;
  storeAddress: string | null;
  storeLat: number | null;
  storeLng: number | null;
  storeUrl: string | null;
  date: string | null;
  extractedItems: any[] | null;
  // false = "Not my trip": someone else's receipt. archive_trip always saves it
  // as a Lattice contribution (pending review), whatever Crowdsourced Prices
  // says, and it stays out of the user's own history.
  isPersonalTrip: boolean;
  attempts: number;
  error: string | null;
  historyId: string | null;
  reward: number | null;
  createdAt: string;
  updatedAt: string;
  // Background worker progress while status is 'processing'.
  stage: 'draft' | 'grounding' | 'finishing' | null;
  groundedCount: number;
  leaseUntil: string | null;
  // As printed on the receipt, for checking the items add up.
  subtotal: number | null;
  totalAmount: number | null;
}

export interface BulkImportBatchRow {
  id: string;
  status: 'processing' | 'complete' | 'blocked';
  totalCount: number;
  processedCount: number;
  createdAt: string;
  // The "My trips" / "Not my trips" choice made at upload; new receipts in the
  // batch start with it and each can be changed on its own.
  defaultIsPersonalTrip: boolean;
}

const mapReceiptRow = (r: any): BulkImportReceiptRow => ({
  id: r.id,
  batchId: r.batch_id,
  imagePath: r.image_path,
  status: r.status,
  storeName: r.store_name,
  storeAddress: r.store_address,
  storeLat: r.store_lat,
  storeLng: r.store_lng,
  storeUrl: r.store_url,
  date: r.date,
  extractedItems: r.extracted_items,
  isPersonalTrip: r.is_personal_trip !== false,
  attempts: r.attempts,
  error: r.error,
  historyId: r.history_id,
  reward: r.reward,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  stage: r.stage ?? null,
  groundedCount: r.grounded_count ?? 0,
  leaseUntil: r.lease_until ?? null,
  subtotal: r.subtotal != null ? Number(r.subtotal) : null,
  totalAmount: r.total_amount != null ? Number(r.total_amount) : null,
});

const mapBatchRow = (b: any): BulkImportBatchRow => ({
  id: b.id,
  status: b.status,
  totalCount: b.total_count,
  processedCount: b.processed_count,
  createdAt: b.created_at,
  defaultIsPersonalTrip: b.default_is_personal_trip !== false,
});

const NON_TERMINAL_RECEIPT_STATUSES = ['queued', 'processing', 'confirmed', 'archiving'];
const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 4; // matches the worker's cap
const KICK_COOLDOWN_MS = 20000;

// Receipts waiting for the worker with nobody working on them right now (no
// live lease) — the worker stops at the end of each run, so while the screen
// is open the browser starts the next run instead of waiting for the cron.
const needsWorker = (rows: BulkImportReceiptRow[]) => rows.some(r =>
  NON_TERMINAL_RECEIPT_STATUSES.includes(r.status) &&
  r.attempts < MAX_ATTEMPTS &&
  (!r.leaseUntil || new Date(r.leaseUntil).getTime() < Date.now())
);

/**
 * Poll-on-reopen (not Realtime, per v1 scope) status source for Bulk Import.
 * Fetches the user's batches/receipts on mount and re-polls every ~3s while
 * anything is non-terminal, stopping once everything settles.
 */
export function useBulkImportPolling(supabaseClient: SupabaseClient, userId: string) {
  const [batches, setBatches] = useState<BulkImportBatchRow[]>([]);
  const [receipts, setReceipts] = useState<BulkImportReceiptRow[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const lastKickRef = useRef(0);
  const fetchSequenceRef = useRef(0);

  const fetchOnce = useCallback(async () => {
    const sequence = ++fetchSequenceRef.current;
    const [{ data: batchRows, error: batchError }, { data: receiptRows, error: receiptError }] = await Promise.all([
      supabaseClient.from('bulk_import_batches').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      // draft holds the worker's raw read of the receipt — not needed here.
      supabaseClient.from('bulk_import_receipts').select('id, batch_id, user_id, image_path, status, store_name, store_address, store_lat, store_lng, store_url, date, extracted_items, is_personal_trip, attempts, error, history_id, reward, created_at, updated_at, stage, grounded_count, lease_until, subtotal, total_amount').eq('user_id', userId).order('created_at', { ascending: true }),
    ]);
    if (!mountedRef.current || sequence !== fetchSequenceRef.current) return;
    if (batchError || receiptError) {
      console.warn('[BulkImport] Status refresh failed:', batchError || receiptError);
      return; // Retain the queue on transient network failures.
    }
    const rows = (receiptRows || []).map(mapReceiptRow);
    setBatches((batchRows || []).map(mapBatchRow));
    setReceipts(rows);
    setIsLoaded(true);
    if (needsWorker(rows) && Date.now() - lastKickRef.current > KICK_COOLDOWN_MS) {
      lastKickRef.current = Date.now();
      kickBulkImportProcessing();
    }
  }, [supabaseClient, userId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    return () => { mountedRef.current = false; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!isLoaded) return;
    const hasNonTerminal = receipts.some(r => NON_TERMINAL_RECEIPT_STATUSES.includes(r.status));
    if (!hasNonTerminal) return;
    timerRef.current = setTimeout(fetchOnce, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [isLoaded, receipts, fetchOnce]);

  return { batches, receipts, isLoaded, refetch: fetchOnce };
}

export interface BulkImportIndicatorState {
  batchId: string | null;
  status: 'processing' | 'complete' | 'blocked' | null;
  hasFailure: boolean; // any receipt in this batch ended 'failed'
  pendingCount: number;
  archivingCount: number;
}

const INDICATOR_POLL_INTERVAL_MS = 10000;

/**
 * Lightweight, always-on poll (independent of BulkImportView being mounted) for
 * the topbar indicator — checks active receipts across batches. Lighter
 * cadence than useBulkImportPolling's 3s in-view poll since this runs for the
 * whole app session, not just while actively watching.
 */
export function useBulkImportStatusIndicator(supabaseClient: SupabaseClient | null | undefined, userId: string | null | undefined): BulkImportIndicatorState {
  const [state, setState] = useState<BulkImportIndicatorState>(() => bulkImportProgress([]));
  useEffect(() => {
    setState(bulkImportProgress([]));
    if (!supabaseClient || !userId) return;
    let cancelled = false;
    let fetching = false;
    let refreshQueued = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (fetching) { refreshQueued = true; return; }
      fetching = true;
      try {
        // One joined read includes older batches still archiving after a newer upload.
        const { data, error } = await supabaseClient.from('bulk_import_batches')
          .select('id, status, bulk_import_receipts(status)')
          .eq('user_id', userId).order('created_at', { ascending: false });
        if (error) throw error;
        if (!cancelled) setState(bulkImportProgress(data || []));
      } catch (error) {
        console.warn('[BulkImport] Background status refresh failed:', error);
      } finally {
        fetching = false;
        if (!cancelled) {
          if (refreshQueued) { refreshQueued = false; void poll(); }
          else timer = setTimeout(poll, INDICATOR_POLL_INTERVAL_MS);
        }
      }
    };
    const refresh = () => { clearTimeout(timer); void poll(); };
    refresh();
    window.addEventListener(BULK_IMPORT_UPDATED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener(BULK_IMPORT_UPDATED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [supabaseClient, userId]);
  return state;
}

/**
 * Starts the background worker (POST /api/bulk-import/kick replies right away
 * and keeps working server-side). Safe to call repeatedly: receipts already
 * being worked on are skipped. The cron task is the backstop when the app is
 * closed. Mirrors the Clerk-token fetch pattern of marketDiscovery.ts.
 */
export async function kickBulkImportProcessing(): Promise<void> {
  try {
    let token: string | null = null;
    try {
      if ((window as any).Clerk?.session) {
        token = await (window as any).Clerk.session.getToken({ template: 'supabase' });
      }
    } catch (e) {
      console.warn('[BulkImport] Failed to get Clerk token for kick request:', e);
    }
    const response = await fetch('/api/bulk-import/kick', {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!response.ok) throw new Error(`Worker request failed (${response.status})`);
  } catch (err) {
    console.warn('[BulkImport] kick request failed (regular cron will still pick this up):', err);
  }
}
